// src/utils/innerCircleClaims.ts
//
// Tracks which Inner Circle results a submitted claim covers. One claim issues every
// outstanding prize for the user, so a claim covers all wins pending when it was
// submitted. Wins that become pending later stay claimable. Result IDs are unique and
// belong to one user (and follow the result when a temp user registers), so they also
// scope the state to the user.

import { fetchInnerCircleState } from "../api/auth";

const CLAIMED_RESULTS_KEY = "inner_circle_claimed_result_ids";

function readClaimedResultIds(): Set<number> {
	try {
		const parsed = JSON.parse(sessionStorage.getItem(CLAIMED_RESULTS_KEY) || "[]");
		return new Set(
			Array.isArray(parsed) ? parsed.filter((id) => Number.isInteger(id)) : [],
		);
	} catch {
		return new Set();
	}
}

function writeClaimedResultIds(ids: Set<number>): void {
	if (ids.size === 0) {
		sessionStorage.removeItem(CLAIMED_RESULTS_KEY);
		return;
	}
	sessionStorage.setItem(CLAIMED_RESULTS_KEY, JSON.stringify([...ids]));
}

export function isInnerCircleResultClaimed(
	resultId: number | null | undefined,
): boolean {
	return resultId != null && readClaimedResultIds().has(resultId);
}

export function markInnerCircleResultsClaimed(resultIds: number[]): void {
	const ids = readClaimedResultIds();
	resultIds.forEach((id) => ids.add(id));
	writeClaimedResultIds(ids);
}

export function unmarkInnerCircleResultsClaimed(resultIds: number[]): void {
	const ids = readClaimedResultIds();
	resultIds.forEach((id) => ids.delete(id));
	writeClaimedResultIds(ids);
}

/**
 * Drops claimed IDs that are no longer pending (i.e. the prize has been issued).
 */
export function pruneClaimedInnerCircleResults(pendingResultIds: number[]): void {
	const pending = new Set(pendingResultIds);
	const ids = readClaimedResultIds();
	const kept = new Set([...ids].filter((id) => pending.has(id)));
	if (kept.size !== ids.size) {
		writeClaimedResultIds(kept);
	}
}

/**
 * Result IDs a claim submitted now would cover: every currently pending win,
 * plus the win the claim was opened for.
 */
export async function getResultIdsCoveredByClaim(
	resultId: number | null | undefined,
): Promise<number[]> {
	const covered = new Set<number>(resultId != null ? [resultId] : []);
	try {
		const state = await fetchInnerCircleState();
		state.pending_wins?.forEach((win) => covered.add(win.result_id));
	} catch (error) {
		console.warn("Could not load pending Inner Circle wins for claim:", error);
	}
	return [...covered];
}
